import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Droplets,
  Phone,
  Mail,
  MapPin,
  MessageSquare,
  Lightbulb,
  Bug,
  Send,
  X,
  ChevronRight,
  ExternalLink,
  Linkedin,
  Twitter,
  Facebook
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// AskIsaac Logo - New [i] Brand Identity
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
// Sterile Mate White Logo
const STERILE_MATE_LOGO_WHITE = "/logos/sterilemate-white.png?v=4";

// Feedback Modal Component
const FeedbackModal = ({ isOpen, onClose, type }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const typeConfig = {
    feedback: {
      title: "Share Your Feedback",
      description: "We value your input! Let us know how we can improve.",
      icon: MessageSquare,
      color: "blue"
    },
    feature: {
      title: "Suggest a Feature",
      description: "Have an idea that would make AskIsaac better? We'd love to hear it!",
      icon: Lightbulb,
      color: "amber"
    },
    bug: {
      title: "Report a Bug",
      description: "Found something that's not working right? Help us fix it.",
      icon: Bug,
      color: "red"
    },
    contact: {
      title: "Contact Us",
      description: "Get in touch with our team. We'd love to hear from you!",
      icon: Mail,
      color: "cyan"
    }
  };

  const config = typeConfig[type] || typeConfig.feedback;
  const Icon = config.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      // Send to backend which will email to info@sterilemate.com
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          type,
          submitted_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        const successMessage = type === "contact" 
          ? "Thank you for contacting us! We'll get back to you soon."
          : "Thank you! Your message has been sent successfully.";
        toast.success(successMessage);
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
        onClose();
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const colorClasses = {
    blue: "bg-blue-600 hover:bg-blue-700",
    amber: "bg-amber-500 hover:bg-amber-600",
    red: "bg-red-600 hover:bg-red-700",
    cyan: "bg-cyan-600 hover:bg-cyan-700"
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              config.color === "blue" ? "bg-blue-100 text-blue-600" :
              config.color === "amber" ? "bg-amber-100 text-amber-600" :
              config.color === "cyan" ? "bg-cyan-100 text-cyan-600" :
              "bg-red-100 text-red-600"
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <DialogTitle className="text-xl">{config.title}</DialogTitle>
          </div>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name *</label>
              <Input
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Email *</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
          </div>
          
          {type === "contact" && (
            <div>
              <label className="text-sm font-medium text-slate-700">Phone</label>
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1"
              />
            </div>
          )}
          
          <div>
            <label className="text-sm font-medium text-slate-700">Subject *</label>
            <Input
              placeholder={type === "contact" ? "How can we help?" : "Brief summary"}
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">Message *</label>
            <Textarea
              placeholder={type === "contact" ? "Tell us about your needs or questions..." : "Tell us more..."}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={4}
              className="mt-1 resize-none"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending}
              className={`flex-1 text-white ${colorClasses[config.color]}`}
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Message
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Footer Component
export default function Footer() {
  const [feedbackModal, setFeedbackModal] = useState({ open: false, type: "feedback" });

  const sitemapSections = [
    {
      title: "Services",
      links: [
        { label: "Maintenance Request", path: "/maintenance" },
        { label: "Order Supplies", path: "/order-request" },
        { label: "Hospital Backorders", path: "/hospital/backorders" },
        { label: "Marketplace", path: "/hospital/marketplace" },
        { label: "Lab Testing", path: "/microbiology-testing" },
        { label: "Water Sampling", path: "/st108" }
      ]
    },
    {
      title: "Portals",
      links: [
        { label: "Hospital Dashboard", path: "/hospital/dashboard" },
        { label: "Contractor Portal", path: "/contractor/login" },
        { label: "Supplier Portal", path: "/supplier/login" },
        { label: "Admin Portal", path: "/admin" },
        { label: "Project Management", path: "/login" }
      ]
    },
    {
      title: "Guides & Docs",
      links: [
        { label: "Telegram Walkthrough", path: "/telegram-walkthrough" },
        { label: "Hospital Guide", path: "/user-guides/hospital" },
        { label: "Contractor Guide", path: "/user-guides/contractor" },
        { label: "Supplier Guide", path: "/user-guides/supplier" },
        { label: "AAMI ST108 Compliance", path: "/st108" }
      ]
    },
    {
      title: "Marketing",
      links: [
        { label: "Demo Gallery", path: "/demos" },
        { label: "How It Works", path: "/how-it-works" },
        { label: "EVS Demo", path: "/demo/evs" },
        { label: "Water Purification Demo", path: "/demo/water-purification" },
        { label: "Investor Portal", path: "/investor" },
        { label: "Sales One Pager", path: "/sales-one-pager" },
        { label: "Pitch Deck", path: "/pitch-deck" }
      ]
    },
    {
      title: "Company",
      links: [
        { label: "About AskIsaac", path: "/about" },
        { label: "Contact Us", action: "contact" },
        { label: "User Guides", path: "/help" },
        { label: "AI Assistant", path: "/ai-assistant" }
      ]
    }
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="site-footer">
      {/* Feedback Bar */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-300 text-sm">
              We're always looking to improve. Share your thoughts with us!
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setFeedbackModal({ open: true, type: "feedback" })}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
                data-testid="feedback-btn"
              >
                <MessageSquare className="w-4 h-4" />
                Give Feedback
              </button>
              <button
                onClick={() => setFeedbackModal({ open: true, type: "feature" })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-sm font-medium transition-colors"
                data-testid="feature-request-btn"
              >
                <Lightbulb className="w-4 h-4" />
                Suggest Feature
              </button>
              <button
                onClick={() => setFeedbackModal({ open: true, type: "bug" })}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
                data-testid="report-bug-btn"
              >
                <Bug className="w-4 h-4" />
                Report Bug
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand Column */}
          <div className="col-span-2">
            {/* AskIsaac Logo with Intelligence Platform */}
            <div className="flex flex-col gap-3 mb-6">
              {/* AskIsaac Logo */}
              <div className="flex items-center gap-2">
                <img 
                  src={ASKISAAC_LOGO} 
                  alt="AskIsaac" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-slate-400 text-xs tracking-widest uppercase">The Intelligence Platform of Sterile Mate LLC</p>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href="tel:800-988-3892" 
                className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                800.988.3892 <span className="text-slate-500 text-xs">(Toll Free)</span>
              </a>
              <a 
                href="tel:281-456-3458" 
                className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                281.456.3458 <span className="text-slate-500 text-xs">(Direct)</span>
              </a>
              <a 
                href="mailto:info@sterilemate.com" 
                className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                info@sterilemate.com
              </a>
              <div className="flex items-start gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>396 W Greens Road Suite 1139<br/> Houston, TX 77067</span>
              </div>
            </div>
          </div>

          {/* Sitemap Columns */}
          {sitemapSections.map((section) => (
            <div key={section.title}>
              <h4 className="text-white font-semibold mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.action ? (
                      <button
                        onClick={() => setFeedbackModal({ open: true, type: link.action })}
                        className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm group"
                      >
                        {link.label}
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </button>
                    ) : link.external ? (
                      <a
                        href={link.path}
                        target={link.path.startsWith("http") ? "_blank" : undefined}
                        rel={link.path.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm group"
                      >
                        {link.label}
                        {link.path.startsWith("http") && (
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </a>
                    ) : link.scroll ? (
                      <a
                        href={link.path}
                        className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm group"
                      >
                        {link.label}
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm group"
                      >
                        {link.label}
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-slate-400 text-sm font-medium">
                The Hospital Needs You Now. Are You Ready?
              </p>
              <p className="text-slate-500 text-xs mt-1">
                © 2026 AskIsaac Inc. All rights reserved.
              </p>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/join-network" className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors">
                Join the Network
              </Link>
              <span className="text-slate-600">•</span>
              <a 
                href="https://sterilemate.com/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-slate-600">•</span>
              <a 
                href="https://sterilemate.com/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </a>
              <span className="text-slate-600">•</span>
              <Link
                to="/medical-device-complaint-form"
                data-testid="footer-complaint-form-link"
                className="text-amber-300 hover:text-amber-200 text-sm font-semibold transition-colors"
              >
                Medical Device Complaint Form
              </Link>
            </div>
          </div>
          
          {/* HIPAA Compliance Notice */}
          <div className="mt-6 pt-6 border-t border-slate-800">
            <div className="flex items-start gap-3 bg-slate-800/50 rounded-lg p-4">
              <div className="w-8 h-8 bg-emerald-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-1">Security & Compliance</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  AskIsaac is designed with healthcare security best practices. This platform does not collect, store, or transmit any Protected Health Information (PHI) or patient data. All service requests are processed without patient identifiers in compliance with HIPAA guidelines.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.open}
        onClose={() => setFeedbackModal({ ...feedbackModal, open: false })}
        type={feedbackModal.type}
      />
    </footer>
  );
}
